import * as types from '../types';
import { ITechnicianApi } from '../contracts';
import { HttpApi } from './HttpApi';
import { withJsonErrorBody } from './attachments';

/** Multipart body: the printed name first (so the server has it before the file part), then the PNG. */
function signatureForm(fileField: 'signature' | 'file', signature: types.TechnicianSignatureInput): FormData {
  const form = new FormData();
  form.append('signerName', signature.signerName);
  form.append(fileField, signature.blob, 'signature.png');
  return form;
}

export class TechnicianHttpApi extends HttpApi implements ITechnicianApi {
  async getTechnicianToday(query: types.TechnicianTodayQuery = {}): Promise<types.TechnicianToday> {
    const params: Record<string, string> = {};
    if (query.date) params.date = query.date;
    if (query.userId) params.userId = query.userId;
    const response = await this.http.get<types.APIResponse<types.TechnicianToday>>('/technician/today', { params });
    return response.data.data;
  }

  async getTechnicianJob(jobId: string): Promise<types.TechnicianJobCard> {
    const response = await this.http.get<types.APIResponse<types.TechnicianJobCard>>(`/technician/jobs/${encodeURIComponent(jobId)}`);
    return response.data.data;
  }

  async startTechnicianJob(jobId: string, options: { confirmOffSchedule?: boolean } = {}): Promise<types.StartTechnicianJobResult> {
    const body = options.confirmOffSchedule ? { confirmOffSchedule: true } : {};
    const response = await this.http.post<types.APIResponse<types.StartTechnicianJobResult>>(`/jobs/${encodeURIComponent(jobId)}/start`, body);
    return response.data.data;
  }

  async stopTechnicianJob(jobId: string): Promise<types.StopTechnicianJobResult> {
    const response = await this.http.post<types.APIResponse<types.StopTechnicianJobResult>>(`/jobs/${encodeURIComponent(jobId)}/stop`, {});
    return response.data.data;
  }

  async finishTechnicianJob(jobId: string, options: types.FinishTechnicianJobOptions = {}): Promise<types.FinishTechnicianJobResult> {
    const url = `/jobs/${encodeURIComponent(jobId)}/finish`;
    const { signature } = options;
    if (!signature) {
      const response = await this.http.post<types.APIResponse<types.FinishTechnicianJobResult>>(url, {});
      return response.data.data;
    }
    const response = await this.http.post<types.APIResponse<types.FinishTechnicianJobResult>>(
      url,
      signatureForm('signature', signature),
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.data;
  }

  async addTechnicianNote(jobId: string, text: string): Promise<types.TechnicianNoteResult> {
    const response = await this.http.post<types.APIResponse<types.TechnicianNoteResult>>(`/jobs/${encodeURIComponent(jobId)}/tech-notes`, { text });
    return response.data.data;
  }

  async uploadTechnicianSignature(jobId: string, signature: types.TechnicianSignatureInput): Promise<types.JobSignature> {
    const response = await this.http.post<types.APIResponse<types.JobSignature>>(
      `/jobs/${encodeURIComponent(jobId)}/signature`,
      signatureForm('file', signature),
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.data;
  }

  async listTechnicianSignatures(jobId: string): Promise<types.JobSignatureList> {
    const response = await this.http.get<types.APIResponse<types.JobSignatureList>>(`/jobs/${encodeURIComponent(jobId)}/signatures`);
    return response.data.data;
  }

  async downloadTechnicianSignatureImage(jobId: string, signatureId: string): Promise<Blob> {
    try {
      const response = await this.http.get<Blob>(
        `/jobs/${encodeURIComponent(jobId)}/signatures/${encodeURIComponent(signatureId)}/image`,
        { responseType: 'blob' },
      );
      return response.data;
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  }
}
